import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCubes, faArrowLeft, faEye, faFileAlt, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const CoarseAggregateTestResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testData, setTestData] = useState({});

  const testConfigurations = {
    sieveAnalysis: {
      name: 'Sieve Analysis',
      description: 'Determination of particle size distribution',
      color: 'var(--vitrag-primary)'
    },
    crushingValue: {
      name: 'Aggregate Crushing Value',
      description: 'Determination of resistance to crushing',
      color: 'var(--vitrag-success)'
    },
    impactValue: {
      name: 'Aggregate Impact Value',
      description: 'Determination of resistance to impact',
      color: 'var(--vitrag-warning)'
    },
    flakinessElongation: {
      name: 'Determination of particle shape characteristics',
      description: 'Flakiness & Elongation Index',
      color: 'var(--vitrag-danger)'
    },
    bulkDensity: {
      name: 'Bulk Density',
      description: 'Determination of Bulk Density (Rodded & Loose)',
      color: 'var(--vitrag-info)'
    },
    specificGravity: {
      name: 'Specific Gravity & Water Absorption',
      description: 'Determination of Specific Gravity & Water Absorption',
      color: '#9C27B0'
    }
  };

  const testOrderMap = {
    sieveAnalysis: 1,
    crushingValue: 2,
    impactValue: 3,
    flakinessElongation: 4,
    bulkDensity: 5,
    specificGravity: 6
  };

  useEffect(() => {
    if (location.state && location.state.selectedTests) {
      const sortedTests = [...location.state.selectedTests].sort((a, b) => {
        const orderA = testOrderMap[a] || 999;
        const orderB = testOrderMap[b] || 999;
        return orderA - orderB;
      });
      setSelectedTests(sortedTests);
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        const mock = {};
        sortedTests.forEach((t) => {
          mock[t] = {
            status: 'completed',
            completedDate: new Date().toLocaleDateString(),
            observations: 'Test observations completed successfully'
          };
        });
        setTestData(mock);
      }, 1200);
    } else {
      navigate('/coarse-aggregate-form');
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 768px) {
        .test-result-card { margin-bottom: 1rem; }
        .test-result-card .card-body { padding: 1rem; }
        .test-result-card .btn { font-size: 0.875rem; padding: 0.5rem 1rem; }
      }
      .test-result-card { transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out; }
      .test-result-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
      .btn-observation { transition: all 0.2s ease-in-out; }
      .btn-observation:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,123,255,0.3); }
      .btn-report { transition: all 0.2s ease-in-out; }
      .btn-report:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,123,255,0.3); }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const handleViewObservationSheet = (testId) => {
    const observationSheetPaths = {
      sieveAnalysis: '/CoarseAggregateTestResults/ObservationSheets/SieveAnalysisObservationSheet.html',
      crushingValue: '/CoarseAggregateTestResults/ObservationSheets/CrushingValueObservationSheet.html',
      impactValue: '/CoarseAggregateTestResults/ObservationSheets/ImpactValueObservationSheet.html',
      flakinessElongation: '/CoarseAggregateTestResults/ObservationSheets/FlakinessElongationObservationSheet.html',
      bulkDensity: '/CoarseAggregateTestResults/ObservationSheets/BulkDensityObservationSheet.html',
      specificGravity: '/CoarseAggregateTestResults/ObservationSheets/SpecificGravityObservationSheet.html'
    };
    const path = observationSheetPaths[testId];
    if (path) {
      const formData = location.state?.formData || {};
      const testRequestId = location.state?.testRequestId;
      
      // Build URL parameters
      const params = new URLSearchParams();
      if (testRequestId) {
        params.append('test_request_id', testRequestId);
      }
      params.append('test_type', testId);
      params.append('customer_name', formData.customer_name || '');
      params.append('site_name', formData.site_name || formData.customer_site_address || '');
      params.append('sample_description', formData.sample_description || '');
      params.append('date_of_receipt', formData.date_of_receipt || formData.date_of_material_receipt || '');
      params.append('date_of_testing', formData.date_of_testing || '');
      params.append('job_code_number', formData.job_code_number || '');
      params.append('tested_by', formData.tested_by_name || '');
      params.append('checked_by', formData.checked_by_name || '');
      params.append('test_remarks', formData.remarks || '');
      
      window.open(`${path}?${params.toString()}`, '_blank');
    } else {
      alert('Observation sheet will be available in the final report.');
    }
  };

  const handleViewTestReport = () => {
    const formData = location.state?.formData || {};
    const testRequestId = location.state?.testRequestId;
    const sampleDescription = (formData.sample_description || '10 MM').trim();
    
    // Normalize sample description for matching (case-insensitive)
    const normalizedSampleDesc = sampleDescription.toLowerCase().replace(/\s+/g, ' ');
    const descLower = sampleDescription.toLowerCase();
    
    // Map sample description to report file name (case-insensitive matching)
    // Include both exact matches and normalized versions
    const reportFileMap = {
      // Exact matches (as stored in form)
      '10 MM': '10mmTestReport.html',
      '20 MM': '20mmTestReport.html',
      'Psand': 'PsandTestReport.html',
      'Msand': 'MsandTestReport.html',
      // Normalized versions
      '10 mm': '10mmTestReport.html',
      '10mm': '10mmTestReport.html',
      '20 mm': '20mmTestReport.html',
      '20mm': '20mmTestReport.html',
      'psand': 'PsandTestReport.html',
      'msand': 'MsandTestReport.html'
    };
    
    // Try exact match first, then normalized match, then keyword matching
    let reportFile = reportFileMap[sampleDescription] || 
                     reportFileMap[normalizedSampleDesc] || 
                     reportFileMap[descLower] ||
                     null;
    
    // Fallback: check if it contains keywords
    if (!reportFile) {
      if (descLower.includes('10') && descLower.includes('mm') && !descLower.includes('20')) {
        reportFile = '10mmTestReport.html';
      } else if (descLower.includes('20') && descLower.includes('mm')) {
        reportFile = '20mmTestReport.html';
      } else if (descLower.includes('psand') || descLower.includes('p sand')) {
        reportFile = 'PsandTestReport.html';
      } else if (descLower.includes('msand') || descLower.includes('m sand')) {
        reportFile = 'MsandTestReport.html';
      } else {
        // Default fallback
        reportFile = '10mmTestReport.html';
      }
    }
    
    // Log for debugging
    console.log('📊 Test Report Selection:', {
      sampleDescription,
      normalizedSampleDesc,
      descLower,
      selectedReport: reportFile,
      formDataSampleDesc: formData.sample_description
    });
    
    const reportType = sampleDescription.toLowerCase().replace(/\s+/g, ''); // "10mm", "20mm", "psand", "msand"
    
    const params = new URLSearchParams();
    
    // Add test_request_id for API fetching
    if (testRequestId) {
      params.append('test_request_id', testRequestId);
    }
    
    // Use appropriate parameter names based on report type
    if (reportType === '10mm') {
      params.append('customer_name_10mm', formData.customer_name || '');
      params.append('site_address_10mm', formData.site_address || formData.customer_site_address || '');
      params.append('date_of_report_10mm', formData.date_of_report || new Date().toLocaleDateString('en-GB'));
      params.append('ulr_number_10mm', formData.ulr_number || formData.url_number || '');
      params.append('reference_number_10mm', formData.reference_number || '');
      params.append('job_code_number_10mm', formData.job_code_number || '');
      params.append('date_of_material_receipt_10mm', formData.date_of_material_receipt || formData.date_of_receipt || '');
      params.append('period_of_testing_10mm', formData.period_of_testing || '');
      params.append('material_description_10mm', formData.material_description || sampleDescription);
      params.append('condition_of_sample_10mm', formData.condition_of_sample || '');
      params.append('location_of_testing_10mm', formData.location_of_testing || 'Lab');
      params.append('source_of_material_10mm', formData.source_of_material || '');
      params.append('reference_number_2_10mm', formData.reference_number_2 || '');
      params.append('test_method_10mm', formData.test_method || 'IS 2386 (Part 1) 1963 (Reaffirmed 2016)');
    } else if (reportType === '20mm') {
      params.append('customer_name_20mm', formData.customer_name || '');
      params.append('site_address_20mm', formData.site_address || formData.customer_site_address || '');
      params.append('date_of_report_20mm', formData.date_of_report || new Date().toLocaleDateString('en-GB'));
      params.append('ulr_number_20mm', formData.ulr_number || formData.url_number || '');
      params.append('reference_number_20mm', formData.reference_number || '');
      params.append('job_code_number_20mm', formData.job_code_number || '');
      params.append('date_of_material_receipt_20mm', formData.date_of_material_receipt || formData.date_of_receipt || '');
      params.append('period_of_testing_20mm', formData.period_of_testing || '');
      params.append('material_description_20mm', formData.material_description || sampleDescription);
      params.append('condition_of_sample_20mm', formData.condition_of_sample || '');
      params.append('location_of_testing_20mm', formData.location_of_testing || 'Lab');
      params.append('source_of_material_20mm', formData.source_of_material || '');
      params.append('reference_number_2_20mm', formData.reference_number_2 || '');
      params.append('test_method_20mm', formData.test_method || 'IS 2386 (Part 1) 1963 (Reaffirmed 2016)');
    } else if (reportType === 'psand') {
      params.append('customer_name_psand', formData.customer_name || '');
      params.append('site_address_psand', formData.site_address || formData.customer_site_address || '');
      params.append('date_of_report_psand', formData.date_of_report || new Date().toLocaleDateString('en-GB'));
      params.append('ulr_number_psand', formData.ulr_number || formData.url_number || '');
      params.append('reference_number_psand', formData.reference_number || '');
      params.append('job_code_number_psand', formData.job_code_number || '');
      params.append('date_of_material_receipt_psand', formData.date_of_material_receipt || formData.date_of_receipt || '');
      params.append('period_of_testing_psand', formData.period_of_testing || '');
      params.append('material_description_psand', formData.material_description || sampleDescription);
      params.append('condition_of_sample_psand', formData.condition_of_sample || '');
      params.append('location_of_testing_psand', formData.location_of_testing || 'Lab');
      params.append('source_of_material_psand', formData.source_of_material || '');
      params.append('reference_number_2_psand', formData.reference_number_2 || '');
      params.append('test_method_psand', formData.test_method || 'IS 2386 (Part 1) 1963 (Reaffirmed 2016)');
    } else if (reportType === 'msand') {
      params.append('customer_name_msand', formData.customer_name || '');
      params.append('site_address_msand', formData.site_address || formData.customer_site_address || '');
      params.append('date_of_report_msand', formData.date_of_report || new Date().toLocaleDateString('en-GB'));
      params.append('ulr_number_msand', formData.ulr_number || formData.url_number || '');
      params.append('reference_number_msand', formData.reference_number || '');
      params.append('job_code_number_msand', formData.job_code_number || '');
      params.append('date_of_material_receipt_msand', formData.date_of_material_receipt || formData.date_of_receipt || '');
      params.append('period_of_testing_msand', formData.period_of_testing || '');
      params.append('material_description_msand', formData.material_description || sampleDescription);
      params.append('condition_of_sample_msand', formData.condition_of_sample || '');
      params.append('location_of_testing_msand', formData.location_of_testing || 'Lab');
      params.append('source_of_material_msand', formData.source_of_material || '');
      params.append('reference_number_2_msand', formData.reference_number_2 || '');
      params.append('test_method_msand', formData.test_method || 'IS 2386 (Part 1) 1963 (Reaffirmed 2016)');
    }
    
    // Open the report in a new tab
    window.open(`/CoarseAggregateTestResults/${reportFile}?${params.toString()}`, '_blank');
  };

  const handleBackToForm = () => {
    navigate('/coarse-aggregate-form');
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#1C2333' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
          <h5 className="text-white">Processing Test Data...</h5>
          <p className="text-muted">Please wait while we prepare your test results</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--vitrag-main-bg)' }}>
      <div className="py-4" style={{ backgroundColor: 'var(--vitrag-secondary)' }}>
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={8}>
              <div className="d-flex align-items-center flex-wrap">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center me-3 mb-2 mb-md-0"
                  style={{ width: '60px', height: '60px', backgroundColor: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.3)' }}
                >
                  <FontAwesomeIcon icon={faCubes} size="2x" className="text-white" />
                </div>
                <div>
                  <h2 className="mb-1 text-white fw-bold">Coarse Aggregate Testing - Results</h2>
                  <p className="mb-0 text-white-50">Test data has been saved successfully</p>
                </div>
              </div>
            </Col>
            <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0"></Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        <Row className="mb-4">
          <Col>
            <Alert variant="success" className="border-0 shadow-sm alert-vitrag" style={{ backgroundColor: 'var(--vitrag-alert-success-bg)', borderColor: 'var(--vitrag-alert-success-border)', color: 'var(--vitrag-alert-success-text)' }}>
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="me-3" style={{ color: 'var(--vitrag-success)' }} size="lg" />
                <div>
                  <h5 className="mb-1" style={{ color: 'var(--vitrag-alert-success-text)' }}>Test Data Saved Successfully!</h5>
                  <p className="mb-0" style={{ color: 'var(--vitrag-alert-success-text)' }}>All selected tests have been completed and data has been saved to the system.</p>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Card className="border-0 shadow-sm test-result-card card-vitrag">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary-bg)', borderBottom: '1px solid var(--vitrag-card-border)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-primary-text)' }}>
                  <FontAwesomeIcon icon={faCubes} className="me-2" style={{ color: 'var(--vitrag-gold)' }} />
                  Selected Tests ({selectedTests.length})
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                {selectedTests.map((testId, index) => {
                  const cfg = testConfigurations[testId];
                  if (!cfg) return null;
                  return (
                    <div key={testId} className={`p-4 ${index !== selectedTests.length - 1 ? 'border-bottom' : ''}`} style={{ backgroundColor: index % 2 === 0 ? 'var(--vitrag-card-bg)' : 'var(--vitrag-secondary-bg)' }}>
                      <Row className="align-items-center">
                        <Col xs={12} md={8}>
                          <div className="d-flex align-items-center flex-wrap">
                            <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3 mb-2 mb-md-0" style={{ width: '50px', height: '50px', backgroundColor: cfg.color + '20', border: `2px solid ${cfg.color}` }}>
                              <FontAwesomeIcon icon={faCubes} style={{ color: cfg.color }} />
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-bold" style={{ color: 'var(--vitrag-primary-text)' }}>{cfg.name}</h6>
                              <p className="mb-0 small" style={{ color: 'var(--vitrag-secondary-text)' }}>{cfg.description}</p>
                              <div className="d-flex align-items-center mt-1 flex-wrap">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1" style={{ color: 'var(--vitrag-success)' }} size="sm" />
                                <span className="small fw-medium" style={{ color: 'var(--vitrag-success)' }}>Completed</span>
                                <span className="small ms-2" style={{ color: 'var(--vitrag-secondary-text)' }}>{testData[testId]?.completedDate}</span>
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewObservationSheet(testId)}
                            className="d-flex align-items-center w-100 w-md-auto justify-content-center btn-observation"
                            style={{ borderColor: 'var(--vitrag-gold)', color: 'var(--vitrag-gold)', backgroundColor: 'transparent' }}
                            onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--vitrag-gold)'; e.target.style.color = 'var(--vitrag-primary-text)'; }}
                            onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = 'var(--vitrag-gold)'; }}
                          >
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            View Observation Sheet
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card className="border-0 shadow-sm card-vitrag">
              <Card.Header style={{ backgroundColor: 'var(--vitrag-secondary-bg)', borderBottom: '1px solid var(--vitrag-card-border)' }}>
                <h5 className="mb-0" style={{ color: 'var(--vitrag-primary-text)' }}>
                  <FontAwesomeIcon icon={faFileAlt} className="me-2" style={{ color: 'var(--vitrag-info)' }} />
                  Comprehensive Test Report
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col xs={12} md={8}>
                    <h6 className="mb-2 fw-bold" style={{ color: 'var(--vitrag-primary-text)' }}>Generate Complete Test Report</h6>
                    <p className="mb-0" style={{ color: 'var(--vitrag-secondary-text)' }}>View a comprehensive report containing all test results, observations, and analysis for the selected tests.</p>
                  </Col>
                  <Col xs={12} md={4} className="text-md-end mt-3 mt-md-0">
                    <Button variant="primary" size="lg" onClick={handleViewTestReport} className="d-flex align-items-center w-100 w-md-auto justify-content-center btn-report btn-vitrag-primary">
                      <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                      View Test Report
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col className="text-center">
            <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3">
              <Button
                variant="outline-secondary"
                onClick={handleBackToForm}
                className="d-flex align-items-center"
                style={{ minWidth: '150px', borderColor: 'var(--vitrag-secondary)', color: 'var(--vitrag-secondary)', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = 'var(--vitrag-secondary)'; e.target.style.color = 'var(--vitrag-primary-text)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = 'var(--vitrag-secondary)'; }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Form
              </Button>
              <Button variant="success" onClick={() => navigate('/other-services-dashboard')} className="d-flex align-items-center btn-vitrag-primary" style={{ minWidth: '150px' }}>
                <FontAwesomeIcon icon={faCubes} className="me-2" />
                Go to Dashboard
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CoarseAggregateTestResults;
